import type { GoalDodReadinessLine, GoalWithSubtasks, SubtaskWithResult } from './types';

// #2749: what the Web goal detail shows, read the same way as the extension's
// goal detail (#2748 board 7). Pure so it can be tested.

export type TimelineActor = 'you' | 'ai' | 'other' | 'event';

export interface TimelineEntry {
  key: string;
  kind: 'history' | 'task' | 'waiting' | 'event';
  actor: TimelineActor;
  /** The person's name when actor is "other". */
  actorName: string | null;
  at: string | null;
  /**
   * An open task that only keeps the goal's deadline (#2681). It is ordered by
   * that date but shows none: the goal's deadline is shown once, in its chip (#2857).
   */
  inheritsDeadline?: boolean;
  text: string;
  /** #2970: on a wait, what is waited for (「承諾と必要情報」); the row shows it after the text. */
  what?: string | null;
  subtask: SubtaskWithResult | null;
  /**
   * Owner 2026-10-28 (choice A): done work of one kind on one day, shown as
   * one line (「12/1 10:00・12/3 15:00 の2枠を仮押さえ」). `subtask` is the latest.
   */
  group?: SubtaskWithResult[];
  /** The kind the group was made by (`doneWorkGroupText`). */
  groupKind?: string;
  sortKey: Array<number | string>;
}

export interface GoalTimeline {
  past: TimelineEntry[];
  upcoming: TimelineEntry[];
}

type Lang = 'ja' | 'en';

function dayKey(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function validTime(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  return Number.isNaN(new Date(value).getTime()) ? null : value;
}

function compareEntries(a: TimelineEntry, b: TimelineEntry): number {
  for (let i = 0; i < Math.max(a.sortKey.length, b.sortKey.length); i += 1) {
    const x = a.sortKey[i] ?? '';
    const y = b.sortKey[i] ?? '';
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
}

/**
 * Splits a goal's history, tasks, wait and event into what already happened
 * and what comes next, each in time order.
 */
export function buildGoalTimeline(goal: GoalWithSubtasks, now: Date = new Date()): GoalTimeline {
  const past: TimelineEntry[] = [];
  const upcoming: TimelineEntry[] = [];
  const today = dayKey(now);
  const intake = goal.simplified_action_intake ?? null;

  (intake?.follow_up_history ?? []).forEach((entry, index) => {
    const line = typeof entry?.line === 'string' ? entry.line.trim() : '';
    if (!line) return;
    const at = validTime(entry.at);
    const mine = entry.sent_by_principal === true;
    past.push({
      key: `history:${index}`, kind: 'history', actor: mine ? 'you' : 'other',
      actorName: mine ? null : (entry.from?.trim() || null),
      at, text: line, subtask: null, sortKey: [at ?? '', 0, index],
    });
  });

  (goal.subtasks || []).filter((st) => st.status !== 'invalidated').forEach((st, index) => {
    const actor: TimelineActor = st.executor === 'ai' ? 'ai' : 'you';
    if (st.status === 'done') {
      const at = validTime(st.done_at);
      past.push({ key: `task:${st.id}`, kind: 'task', actor, actorName: null, at, text: st.title, subtask: st, sortKey: [at ?? '', 1, index] });
      return;
    }
    const own = st.deadline_inherited === false ? validTime(st.deadline) : null;
    const at = own ?? validTime(st.deadline) ?? validTime(goal.deadline);
    // Due today or earlier first, then the wait, then later dates, then undated.
    const bucket = at ? (dayKey(at) <= today ? 0 : 2) : 3;
    upcoming.push({
      key: `task:${st.id}`, kind: 'task', actor, actorName: null, at, inheritsDeadline: !own,
      text: st.title, subtask: st, sortKey: [bucket, at ?? '', index],
    });
  });

  const waiting = intake?.waiting_on;
  if (goal.status === 'active' && waiting && (waiting.who === 'counterparty' || waiting.who === 'someone_else') && waiting.label?.trim()) {
    upcoming.push({
      key: 'waiting', kind: 'waiting', actor: 'other', actorName: waiting.name?.trim() || null,
      at: validTime(waiting.since ?? waiting.read_at), text: waiting.label.trim(), what: waiting.what?.trim() || null,
      subtask: null, sortKey: [1, '', 0],
    });
  }

  const eventAt = validTime(goal.linked_event?.start_at);
  if (eventAt) {
    const entry: TimelineEntry = {
      key: 'event', kind: 'event', actor: 'event', actorName: null, at: eventAt,
      text: goal.linked_event?.title || '', subtask: null, sortKey: [],
    };
    if (new Date(eventAt).getTime() < now.getTime()) past.push({ ...entry, sortKey: [eventAt, 2, 0] });
    else upcoming.push({ ...entry, sortKey: [dayKey(eventAt) <= today ? 0 : 2, eventAt, 1e9] });
  }

  past.sort(compareEntries);
  upcoming.sort(compareEntries);
  return { past: groupDoneWork(past), upcoming };
}

function recordOf(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

/** What kind of work a done task did, for grouping; null when it stands alone. */
function doneWorkKind(st: SubtaskWithResult): string | null {
  const metadata = recordOf(st.metadata);
  const event = recordOf(metadata?.calendar_event);
  if (recordOf(event?.tentative_hold)) return 'hold';
  if (recordOf(event?.release_hold)) return 'release';
  const route = recordOf(metadata?.ai_work_route);
  if (st.executor === 'ai' && route && typeof route.how === 'string') {
    return `route:${route.how}|${String(route.what ?? '')}|${String(route.where ?? '')}`;
  }
  return null;
}

function shortTime(value: unknown): string | null {
  if (typeof value !== 'string' || Number.isNaN(new Date(value).getTime())) return null;
  const date = new Date(value);
  return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** One line for a group of done work of one kind. */
export function doneWorkGroupText(kind: string, group: readonly SubtaskWithResult[], lang: Lang): string {
  // #3581: a grouped hold or release counts each of its times.
  const starts = group.flatMap((st) => {
    const event = recordOf(recordOf(st.metadata)?.calendar_event);
    const slots = Array.isArray(event?.holds) ? event.holds : Array.isArray(event?.release_holds) ? event.release_holds : null;
    return slots ? slots.map((slot) => recordOf(slot)?.start_at) : [event?.start_at];
  });
  if ((kind === 'hold' || kind === 'release') && starts.every((start) => shortTime(start))) {
    const times = [...starts].sort((a, b) => Date.parse(String(a)) - Date.parse(String(b))).map(shortTime);
    if (lang === 'en') return kind === 'hold' ? `Held ${starts.length} slots: ${times.join(', ')}` : `Released ${starts.length} holds: ${times.join(', ')}`;
    return kind === 'hold' ? `${times.join('・')} の${starts.length}枠を仮押さえ` : `${times.join('・')} の${starts.length}枠の仮押さえを外す`;
  }
  const first = group[0]!.title;
  return lang === 'en' ? `${first} (+${group.length - 1} more)` : `${first}ほか${group.length - 1}件`;
}

/**
 * Owner 2026-10-28 (choice A): a secretary reports 「3枠を仮押さえしました」
 * once, not each slot. Done work of the same kind by the same actor on the
 * same day becomes one line, at the latest one's place.
 */
function groupDoneWork(past: TimelineEntry[]): TimelineEntry[] {
  const groups = new Map<string, TimelineEntry[]>();
  for (const entry of past) {
    const st = entry.kind === 'task' ? entry.subtask : null;
    const kind = st && st.status === 'done' && entry.at ? doneWorkKind(st) : null;
    if (!kind || !entry.at) continue;
    const key = `${kind}#${entry.actor}#${dayKey(entry.at)}`;
    groups.set(key, [...(groups.get(key) ?? []), entry]);
  }
  const result: TimelineEntry[] = [];
  for (const entry of past) {
    const st = entry.kind === 'task' ? entry.subtask : null;
    const kind = st && st.status === 'done' && entry.at ? doneWorkKind(st) : null;
    const members = kind && entry.at ? groups.get(`${kind}#${entry.actor}#${dayKey(entry.at)}`) : undefined;
    if (!kind || !members || members.length < 2) {
      result.push(entry);
      continue;
    }
    if (members[members.length - 1] !== entry) continue;
    const group = members.map((member) => member.subtask!);
    result.push({ ...entry, key: `group:${entry.key}`, text: doneWorkGroupText(kind, group, 'ja'), group, groupKind: kind });
  }
  return result;
}

export function timelineDateLabel(entry: TimelineEntry, now: Date, lang: Lang): string {
  if (entry.kind === 'waiting') return lang === 'en' ? 'Waiting' : '待ち';
  if (!entry.at || entry.inheritsDeadline) return '';
  const date = new Date(entry.at);
  if (dayKey(date) === dayKey(now)) return lang === 'en' ? 'Today' : '今日';
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

/** One secretary line on what comes next, from the same timeline. */
export function goalSummaryLine(goal: GoalWithSubtasks, hasJudgment: boolean, lang: Lang, now: Date = new Date()): string {
  const en = lang === 'en';
  const next = buildGoalTimeline(goal, now).upcoming.find((entry) => entry.kind !== 'event');
  if (hasJudgment) return en ? 'There is a decision for you to make.' : 'ご判断いただきたいことがあります。';
  if (next?.kind === 'task' && next.actor === 'ai') return en ? `Next, AI will work on "${next.text}".` : `次は、AIが「${next.text}」を進めます。`;
  if (next?.kind === 'task') return en ? `Next for you: "${next.text}".` : `次は、「${next.text}」をお願いします。`;
  // #3222: whose turn it is shows just above; the line says what is waited for, if known.
  if (next?.kind === 'waiting') return next.what ? (en ? `Waiting for: ${next.what}.` : `「${next.what}」を待っています。`) : '';
  // #2901: a goal whose only work is waiting on the other side has no task row,
  // only the plan's wait. #3222: once the goal says whose turn it is, the turn
  // decides; on the principal's turn a planned wait is not 「待っています」.
  if (!intakeTurnKnown(goal) && (goal.grma_materialization?.waiting_readbacks?.length ?? 0) > 0) {
    return en ? 'Waiting for a reply from the other side.' : 'いまは相手からの返答を待っています。';
  }
  return en ? 'The next step is not decided yet.' : '次にやることがまだ決まっていません。';
}

function intakeTurnKnown(goal: GoalWithSubtasks): boolean {
  return !!goal.simplified_action_intake?.waiting_on;
}

/**
 * Whether the user has a judgment still to make on the goal: a packet not yet
 * answered, a review proposal, or 「誰が進めますか」 without an answer. An
 * answered packet stays in the detail as a record but asks nothing.
 * Legacy decisions are counted by the caller (`openDecisions`).
 */
export function hasOpenJudgment(goal: GoalWithSubtasks): boolean {
  const intake = goal.simplified_action_intake;
  const packets = Array.isArray(intake?.judgment_packets) && intake.judgment_packets.length > 0
    ? intake.judgment_packets.map((entry) => entry.packet)
    : intake?.judgment_packet ? [intake.judgment_packet] : [];
  if (packets.some((packet) => packet?.readiness === 'awaiting_user_judgment' && !packet.selected_option_key)) return true;
  if ((intake?.review_change_proposals?.length ?? 0) > 0) return true;
  return (goal.actor_questions?.conditions ?? []).some((condition) => !condition.answer);
}

function isOpenTask(st: SubtaskWithResult): boolean {
  return st.status !== 'done' && st.status !== 'invalidated';
}

/**
 * Owner 2026-10-28: the user's blocked tasks that can start only after the
 * AI's open work, as the work list reads them (src/lib/work-list.ts
 * waitingOnAiIds): the task's `blocked_by` and its plan's `depends_on_proposal_keys`.
 */
function waitingOnAiIds(subtasks: SubtaskWithResult[]): Set<string> {
  const live = subtasks.filter((st) => st.status !== 'invalidated');
  const byId = new Map(live.map((st) => [st.id, st]));
  const landingOf = (st: SubtaskWithResult) => (st.metadata?.grma_proposal_landing ?? null) as { proposal_key?: unknown; depends_on_proposal_keys?: unknown } | null;
  const byKey = new Map<string, SubtaskWithResult>();
  for (const st of live) {
    const key = landingOf(st)?.proposal_key;
    if (typeof key === 'string' && key) byKey.set(key, st);
  }
  const depsOf = (st: SubtaskWithResult) => {
    const keys = landingOf(st)?.depends_on_proposal_keys;
    return [
      st.blocked_by ? byId.get(st.blocked_by) : undefined,
      ...(Array.isArray(keys) ? keys : []).map((key) => (typeof key === 'string' ? byKey.get(key) : undefined)),
    ].filter((dep): dep is SubtaskWithResult => dep !== undefined && isOpenTask(dep));
  };
  const waits = (st: SubtaskWithResult, seen: Set<string>): boolean => {
    if (st.status !== 'blocked' || seen.has(st.id)) return false;
    seen.add(st.id);
    return depsOf(st).some((dep) => dep.executor === 'ai' ? dep.status !== 'review' : waits(dep, seen));
  };
  return new Set(live.filter((st) => st.executor !== 'ai' && isOpenTask(st) && waits(st, new Set())).map((st) => st.id));
}

/**
 * Owner 2026-10-28: with no judgment open and every task of the user's able
 * to start only after the AI's work, the goal waits on the AI, as the work
 * list puts it under 対応待ち.
 */
function waitsOnAi(goal: GoalWithSubtasks): boolean {
  const open = (goal.subtasks ?? []).filter(isOpenTask);
  if (!open.some((st) => st.executor === 'ai' && st.status !== 'review')) return false;
  const waiting = waitingOnAiIds(goal.subtasks ?? []);
  return !open.some((st) => st.executor !== 'ai' ? !waiting.has(st.id) : st.status === 'review');
}

/** #3222: whose turn the goal is on, shown under its name. */
export interface GoalTurnLine {
  theirs: boolean;
  /** Owner 2026-10-28: the goal waits on the AI's work. */
  ai: boolean;
  /** Who, as the badge says it: 「あなた」, 「AI」 or the other side's name. */
  who: string;
  /** The line after the badge: 「の番です」 / 「の処理待ちです」 / 「の対応待ちです」. */
  text: string;
}

export function goalTurnLine(goal: GoalWithSubtasks, lang: Lang, hasJudgment = false): GoalTurnLine | null {
  if (goal.status !== 'active') return null;
  const en = lang === 'en';
  const waiting = goal.simplified_action_intake?.waiting_on;
  const theirs = waiting?.who === 'counterparty' || waiting?.who === 'someone_else';
  if (!theirs && !hasJudgment && waitsOnAi(goal)) {
    return { theirs: false, ai: true, who: 'AI', text: en ? ' is working on it' : 'の処理待ちです' };
  }
  if (!theirs) {
    return {
      theirs: false,
      ai: false,
      who: en ? 'You' : 'あなた',
      text: en ? "'re up" : 'の番です',
    };
  }
  const name = waiting?.name?.trim() || null;
  const who = waiting?.who === 'someone_else'
    ? (en ? 'Someone else' : 'ほかの方')
    : name ? (en ? name : `${name}さん`) : (en ? 'The other side' : '相手');
  return {
    theirs: true,
    ai: false,
    who,
    text: en ? ' is up' : 'の対応待ちです',
  };
}

/** The completion conditions still in force, with whether each is met. */
export function goalConditions(goal: Pick<GoalWithSubtasks, 'goal_dod_items'>): Array<{ text: string; met: boolean; readiness: GoalDodReadinessLine[] }> {
  return (goal.goal_dod_items ?? [])
    .filter((item) => item && item.tracking !== 'retired' && typeof item.what === 'string' && item.what.trim())
    .map((item) => ({
      text: item.what.trim(),
      met: item.satisfaction === 'satisfied' || item.satisfaction === 'observed',
      // #3560 stage 2: where the meeting is held is ready, shown under the condition.
      readiness: Array.isArray(item.readiness) ? item.readiness.filter((line) => line && typeof line.what === 'string' && line.what.trim()) : [],
    }));
}

/**
 * In progress = yours to do now (tasks and a judgment); waiting = on the AI
 * or someone else; done = finished tasks.
 */
export function goalProgressCounts(timeline: GoalTimeline, hasJudgment: boolean): { active: number; waiting: number; done: number } {
  return {
    active: timeline.upcoming.filter((entry) => entry.kind === 'task' && entry.actor === 'you' && entry.subtask?.status !== 'blocked').length + (hasJudgment ? 1 : 0),
    // A task of the user's that cannot start yet (`blocked`) waits too.
    waiting: timeline.upcoming.filter((entry) => entry.kind === 'waiting' || (entry.kind === 'task' && (entry.actor === 'ai' || entry.subtask?.status === 'blocked'))).length,
    done: timeline.past.filter((entry) => entry.kind === 'task').length,
  };
}

const JUDGMENT_REFRESH_REASON_TEXT: Record<NonNullable<GoalWithSubtasks['judgment_refresh_unavailable']>['reason'], { ja: string; en: string }> = {
  not_mail: { ja: 'メール以外から作ったゴールなので、元の内容を読み直せませんでした。', en: 'This goal did not come from an email, so its source could not be read again.' },
  reconnect: { ja: 'Gmail の接続が切れていて、元のメールを読み直せませんでした。', en: 'Gmail is not connected, so the original email could not be read again.' },
  mail_gone: { ja: '元のメールが見つからず、読み直せませんでした。', en: 'The original email could not be found, so it was not read again.' },
  temporary: { ja: '元のメールを一時的に読み直せませんでした。', en: 'The original email could not be read again for now.' },
};

/**
 * #2282 (owner 2026-10-18): why the tasks were made without reading the
 * source again, and what that means for them.
 */
export function judgmentRefreshNote(refresh: NonNullable<GoalWithSubtasks['judgment_refresh_unavailable']>, en: boolean): string {
  const reason = JUDGMENT_REFRESH_REASON_TEXT[refresh.reason] ?? JUDGMENT_REFRESH_REASON_TEXT.temporary;
  if (en) return `${reason.en} ${refresh.plan_read ? 'The tasks use what was read before.' : 'Please check that no decision is missing from the tasks.'}`;
  return `${reason.ja}${refresh.plan_read ? '前に読み取った内容でタスクを作っています。' : 'タスクに抜けている判断がないか確かめてください。'}`;
}
